import * as turf from "@turf/turf";
import * as JSZip from "jszip";
import * as UtmObj from "utm-latlng";

import { getEnvironment, getMobileOperatingSystem } from "utils";

import { geoSpheres, geoZones } from "enums";

const WGS_84_PRJ = "+title=WGS 84 (long/lat) +proj=longlat +ellps=WGS84 +datum=WGS84 +units=degrees";

export const getGoogleKey = () => {
  const env = getEnvironment();
  const op = getMobileOperatingSystem();

  if (env.app && op === "ios") {
    return process.env.REACT_APP_GOOGLE_IOS_KEY;
  }
  if (env.app && op === "android") {
    return process.env.REACT_APP_GOOGLE_ANDROID_KEY;
  }

  return process.env.REACT_APP_GOOGLE_KEY;
};

export const matchGeoFirstAndLastCoords = (geometry) => {
  // ALWAYS USE THIS
  // When using turfjs, the first and last co-ordinates must be the same
  if (geometry[0] !== geometry[geometry.length - 1]) {
    const newArr = [...geometry, geometry[0]];
    return newArr;
  }
  return geometry;
};

const calculateSize = (geometry) => {
  if (geometry[0] !== geometry[geometry.length - 1]) {
    geometry.push(geometry[0]);
  }

  if (geometry.length >= 3) {
    // note: will fail if shape has less than 3 coordinates
    const reducedShape = geometry.reduce((prev, shape) => [...prev, ...[[shape.lng, shape.lat]]], []); // reduce to an arrray of points for proccessing
    const area = turf.area(turf.polygon([reducedShape])); // calc size in meters
    return area * 0.0001;
  }

  return 0;
};

export const getZoomLevelFromBounds = (geometry) => {
  if (!geometry || !Array.isArray(geometry) || geometry.length === 0) {
    return 14;
  }

  // Calculate bounding box
  const lats = geometry.map((point) => point.lat);
  const lngs = geometry.map((point) => point.lng);

  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);
  const minLng = Math.min(...lngs);
  const maxLng = Math.max(...lngs);

  // Calculate the span in degrees
  const latSpan = maxLat - minLat;
  const lngSpan = maxLng - minLng;
  const maxSpan = Math.max(latSpan, lngSpan);

  // Add 10% padding to ensure geometry doesn't touch edges and is fully visible
  const paddedSpan = maxSpan * 1.1;

  // Map span to zoom level - very conservative to ensure full visibility
  // Lower zoom numbers = more zoomed out
  if (paddedSpan >= 2.0) return 6; // Very large areas (200+ km)
  if (paddedSpan >= 1.0) return 7; // Large areas (100-200 km)
  if (paddedSpan >= 0.5) return 8; // Medium-large (50-100 km)
  if (paddedSpan >= 0.25) return 9; // Medium (25-50 km)
  if (paddedSpan >= 0.1) return 10; // Small-medium (10-25 km)
  if (paddedSpan >= 0.05) return 11; // Small (5-10 km)
  if (paddedSpan >= 0.025) return 12; // 2.5-5 km
  if (paddedSpan >= 0.0125) return 13; // 1.25-2.5 km
  if (paddedSpan >= 0.006) return 14; // 600m-1.25 km
  if (paddedSpan >= 0.003) return 15; // 300-600m
  if (paddedSpan >= 0.0015) return 16; // 150-300m
  if (paddedSpan >= 0.0008) return 17; // 80-150m
  return 18; // Very small fields (<80m)
};

export const getZoomLevel = (field, mini) => {
  let { size } = field;

  const shape = field.geometry && field.geometry.geometry ? field.geometry.geometry : null;
  const getZoomLevel = mini ? "s" : "l";
  const zoomLevels = [
    {
      l: 17,
      s: 16,
    },
    {
      l: 17,
      s: 15,
    },
    {
      l: 16,
      s: 15,
    },
    {
      l: 15,
      s: 14,
    },
    {
      l: 14,
      s: 13,
    },
  ];

  if (shape && shape[0] !== shape[shape.length - 1]) {
    field.geometry.geometry.push(shape[0]);
  }

  if (!size && field.geometry && field.geometry.geometry) {
    size = calculateSize(field.geometry.geometry);
  } else if (!size && field.geometry) {
    size = calculateSize(field.geometry);
  } else if (!size) {
    return 14;
  }

  if (parseFloat(size) <= 2) {
    return zoomLevels[0][getZoomLevel];
  }
  if (parseFloat(size) <= 5) {
    return zoomLevels[1][getZoomLevel];
  }
  if (parseFloat(size) <= 10) {
    return zoomLevels[2][getZoomLevel];
  }
  if (parseFloat(size) <= 20) {
    return zoomLevels[3][getZoomLevel];
  }
  if (parseFloat(size) <= 100) {
    return zoomLevels[4][getZoomLevel];
  }
  return 15;
};

export const getZoomLevelMultipleFields = (fields) => {
  if (fields.length >= 100) {
    return 12;
  }
  if (fields.length >= 50) {
    return 13;
  }
  if (fields.length >= 10) {
    return 14;
  }
  return 15;
};

export const addShapeProcessor = () => {
  let script = document.createElement("script");
  script.src = "/shp/shp.js";
  script.referrerpolicy = "origin";
  document.body.appendChild(script);

  script = document.createElement("script");
  script.src = "/shp/proj4.js";
  script.referrerpolicy = "origin";
  document.body.appendChild(script);
};

export const SHAPEFILE_VALIDATION_ERRORS = {
  INVALID_EXTENSION: "INVALID_EXTENSION",
  INVALID_SIGNATURE: "INVALID_SIGNATURE",
  MISSING_REQUIRED_FILE: "MISSING_REQUIRED_FILE",
  NESTED_ZIP: "NESTED_ZIP",
  BLACKLISTED_FILE: "BLACKLISTED_FILE",
  ZIP_PARSE_FAILED: "ZIP_PARSE_FAILED",
};

const BLACKLISTED_FILES = new Set([".exe", ".py", ".go", ".cpp", ".ts", ".js"]);
const NESTED_ARCHIVE_EXTENSIONS = new Set([".zip", ".rar", ".7z"]);
const REQUIRED_FILES = new Set([".shp", ".shx", ".dbf", ".prj"]);

const validateShapefileContent = async (zip) => {
  const fileExtensions = new Set();

  for (const entry of Object.values(zip.files)) {
    // eslint-disable-next-line no-continue
    if (entry.dir) continue;

    // Extract the file extension from the entry name
    const extension = entry.name.slice(entry.name.lastIndexOf(".")).toLowerCase();
    fileExtensions.add(extension);

    if (NESTED_ARCHIVE_EXTENSIONS.has(extension)) {
      return { isValid: false, reason: SHAPEFILE_VALIDATION_ERRORS.NESTED_ZIP };
    }
  }

  for (const required of REQUIRED_FILES) {
    if (!fileExtensions.has(required)) {
      return {
        isValid: false,
        reason: SHAPEFILE_VALIDATION_ERRORS.MISSING_REQUIRED_FILE,
        meta: required,
      };
    }
  }

  for (const blacklisted of BLACKLISTED_FILES) {
    if (fileExtensions.has(blacklisted)) {
      return {
        isValid: false,
        reason: SHAPEFILE_VALIDATION_ERRORS.BLACKLISTED_FILE,
        meta: blacklisted,
      };
    }
  }

  return { isValid: true };
};

export const checkValidShapefileType = async (file) => {
  try {
    const ZIP_SIGNATURE = "504B0304";

    if (!file?.name?.toLowerCase().endsWith(".zip")) {
      return { isValid: false, reason: SHAPEFILE_VALIDATION_ERRORS.INVALID_EXTENSION, meta: file?.name };
    }

    // Extract the first 4 bytes of the file to check the signature
    const buffer = await file.slice(0, 4).arrayBuffer();
    const signature = Array.from(new Uint8Array(buffer))
      .map((byte) => byte.toString(16).padStart(2, "0"))
      .join("")
      .toUpperCase();

    if (signature !== ZIP_SIGNATURE) {
      return { isValid: false, reason: SHAPEFILE_VALIDATION_ERRORS.INVALID_SIGNATURE, meta: file?.name };
    }

    const zip = new JSZip();
    const content = await zip.loadAsync(file);
    const contentValidation = await validateShapefileContent(content);

    if (!contentValidation.isValid) {
      return contentValidation;
    }

    return { isValid: true };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Error validating shapefile:", error);
    return { isValid: false, reason: SHAPEFILE_VALIDATION_ERRORS.ZIP_PARSE_FAILED, meta: error.message };
  }
};

export const getCenter = (geometry) => {
  const lowestLat = geometry.reduce((prev, curr) => (prev.lat < curr.lat ? prev : curr), geometry[0]).lat;
  const lowestLng = geometry.reduce((prev, curr) => (prev.lng < curr.lng ? prev : curr), geometry[0]).lng;
  const highestLat = geometry.reduce((prev, curr) => (prev.lat > curr.lat ? prev : curr), geometry[0]).lat;
  const highestLng = geometry.reduce((prev, curr) => (prev.lng > curr.lng ? prev : curr), geometry[0]).lng;

  return {
    lng: lowestLng + (highestLng - lowestLng) / 2,
    lat: lowestLat + (highestLat - lowestLat) / 2,
  };
};

const findProjection = async (data) => {
  let projectExists = false;
  const defaultProjection = {
    zoneNum: 32,
    zoneLetter: "N",
    sphere: "WGS 84",
    updated: true,
    prj: false,
  };

  const zipFile = new JSZip();
  return zipFile.loadAsync(data).then(async (files) => {
    for (const key of Object.keys(files.files)) {
      if (key.includes(".prj")) {
        projectExists = true;
      }
    }

    if (projectExists) {
      for (const key of Object.keys(files.files)) {
        if (key.includes(".prj")) {
          return files.files[key].async("string").then(async (project) => {
            if (
              geoZones.find((zone) => project.includes(zone)) &&
              geoSpheres.find((sphere) => project.includes(sphere) || project.toLowerCase().includes(sphere.toLowerCase().replace(" ", "_")))
            ) {
              return {
                zoneNum: parseInt(geoZones.find((zone) => project.includes(zone)).replace(/\D/g, "")),
                zoneLetter: geoZones.find((zone) => project.includes(zone)).slice(-1),
                sphere: geoSpheres.find((sphere) => project.includes(sphere) || project.toLowerCase().includes(sphere.toLowerCase().replace(" ", "_"))),
                prj: project,
              };
            }
            if (geoSpheres.find((sphere) => project.includes(sphere) || project.toLowerCase().includes(sphere.toLowerCase().replace(" ", "_")))) {
              return {
                zoneNum: 32,
                zoneLetter: "N",
                sphere: geoSpheres.find((sphere) => project.includes(sphere) || project.toLowerCase().includes(sphere.toLowerCase().replace(" ", "_"))),
                prj: project,
              };
            }
            return defaultProjection;
          });
        }
      }
    }
    return defaultProjection;
  });
};

const BboxUID = {
  encode: (bbox) => {
    if (!Array.isArray(bbox) || bbox.length < 4) return null;
    try {
      return btoa(JSON.stringify(bbox)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
    } catch {
      return null;
    }
  },

  decode: (uid) => {
    try {
      const decoded = atob(uid.replace(/-/g, "+").replace(/_/g, "/"));
      const bbox = JSON.parse(decoded);
      return Array.isArray(bbox) && bbox.length >= 4 ? bbox : null;
    } catch {
      return null;
    }
  },

  validate: (bbox) => {
    try {
      if (!Array.isArray(bbox) || bbox.length < 4) return false;
      return bbox.every((n) => !Number.isNaN(n) && typeof n === "number");
    } catch {
      return false;
    }
  },
};

const isPolishShapeFile = (feature) => {
  const POLISH_FEATURES = new Set(["ID_przedmiotu", "Nzw_granic", "Pole"]);

  const properties = Object.keys(feature.properties);

  // My current assertions for a Polish shapefile (based on our research so far):
  // Case 1: Has any of the known Polish features above
  // Case 2: Has only 'id' property
  const isPolish = properties.some((key) => POLISH_FEATURES.has(key)) || (properties.length === 1 && properties[0] === "id");
  return !!isPolish;
};

// 🧠 Generated this helper function based on some research on stackoverflow / os projects
// (had to make it a docfile incase we wish to transform this to .ts at some point)
/**
 * Determines if coordinates need UTM conversion based on their values and projection info
 * @param coordinates - [x, y] or [lng, lat] coordinate pair to check
 * @param projection - Projection information object
 * @returns boolean indicating if conversion is needed
 */
const needsUTMConversion = (coordinates, projection = { sphere: "WGS 84" }) => {
  // Handle missing or malformed coordinates
  if (!coordinates || !Array.isArray(coordinates) || coordinates.length < 2) {
    return false;
  }

  const [x, y] = coordinates;

  // Check if values aren't valid numbers
  if (typeof x !== "number" || typeof y !== "number" || Number.isNaN(x) || Number.isNaN(y)) {
    return false;
  }

  // Using non-WGS84 datum/ellipsoid that requires transformation
  if (projection.sphere !== "WGS 84" && projection.sphere !== "WGS84") {
    return true;
  }

  // Coordinates are outside valid lat/lng ranges
  // Valid latitude: -90 to 90 degrees
  // Valid longitude: -180 to 180 degrees
  if (y > 90 || y < -90 || x > 180 || x < -180) {
    return true;
  }

  // Values are likely in UTM or other projected system (large values)
  // Most UTM coordinates are large numbers (easting/northing values)
  if (Math.abs(x) > 1000 || Math.abs(y) > 1000) {
    return true;
  }

  // 3D coordinates (has Z component)
  if (coordinates.length >= 3) {
    return true;
  }

  // we don't need to convert
  return false;
};

// TODO 🚨 : merge with processGeometries when it is stable and tested ( just for polish shapefilesfor now )
export const processGeometriesPL = async (currentData, onError, managementTool) => {
  const projection = await findProjection(currentData);
  let result = false;

  const processGeoData = async (geoData) => {
    const currentGeometries = [];
    let geometry = [];
    let latLong = false;
    let proj4Conversion = false;
    let field = false;

    if (!geoData) return currentGeometries;

    if (projection.sphere === "WGS 84") {
      projection.prj = WGS_84_PRJ;
    }

    if (Array.isArray(geoData)) {
      await geoData.forEach((geo) => {
        if (!geo?.features) return;

        geo.features.forEach((feature) => {
          //  comprehensive null check for geometry coordinates (saw an issue with a shapefile that had a feature with no coordinates)
          if (!feature?.geometry?.coordinates?.[0] || !Array.isArray(feature.geometry.coordinates[0])) {
            // eslint-disable-next-line no-console
            console.warn("Skipping feature with invalid geometry", feature);
            return;
          }

          if (Array.isArray(feature.geometry.coordinates[0])) {
            field = {
              ...feature.properties,
              customUniqueId: BboxUID.validate(feature.geometry?.bbox)
                ? BboxUID.encode(feature.geometry?.bbox)
                : BboxUID.encode(feature.geometry?.coordinates[0]),
            };

            feature.geometry.coordinates.forEach((coordinate) => {
              geometry = [];
              coordinate.forEach((item) => {
                if (typeof item[0] === "number" && typeof item[1] === "number") {
                  latLong = { lat: item[1], lng: item[0] };

                  if (
                    latLong.lat > 90 ||
                    latLong.lat < -90 ||
                    latLong.lng > 90 ||
                    latLong.lat < -90 ||
                    projection.sphere === "Bessel 1841" ||
                    item.length >= 3
                  ) {
                    proj4Conversion = window.proj4(projection.prj, WGS_84_PRJ, [item[0], item[1]]);

                    geometry.push({
                      lat: proj4Conversion[1],
                      lng: proj4Conversion[0],
                    });
                  } else {
                    geometry.push(latLong);
                  }
                }
              });

              if (geometry?.length > 0) {
                currentGeometries.push({
                  geometry: geometry,
                  field: field,
                  center: getCenter(geometry),
                });
              }
            });
          }
        });
        return currentGeometries;
      });
    } else {
      if (!geoData?.features) return currentGeometries;

      await geoData.features.forEach(async (feature) => {
        //  comprehensive null check for geometry coordinates (saw an issue with a shapefile that had a feature with no coordinates)
        if (!feature?.geometry?.coordinates) {
          // eslint-disable-next-line no-console
          console.warn("Skipping feature with missing coordinates", feature);
          return;
        }
        field = {
          ...feature.properties,
          customUniqueId: BboxUID.validate(feature.geometry?.bbox) ? BboxUID.encode(feature.geometry?.bbox) : BboxUID.encode(feature.geometry?.coordinates[0]),
        };
        await feature.geometry.coordinates.forEach((coordinate) => {
          geometry = [];

          coordinate.forEach((item) => {
            if (typeof item[0] === "number" && typeof item[1] === "number" && managementTool === "365FarmNet") {
              geometry.push({
                lat: item[1],
                lng: item[0],
              });
            } else if (typeof item[0] === "number" && typeof item[1] === "number") {
              latLong = { lat: item[1], lng: item[0] };
              if (latLong.lat > 90 || latLong.lat < -90 || latLong.lng > 90 || latLong.lat < -90 || projection.sphere === "Bessel 1841" || item.length >= 3) {
                proj4Conversion = window.proj4(projection.prj, WGS_84_PRJ, [item[0], item[1]]);

                geometry.push({
                  lat: proj4Conversion[1],
                  lng: proj4Conversion[0],
                });
              } else {
                geometry.push(latLong);
              }
            }
          });
          if (geometry.length > 0) {
            currentGeometries.push({
              geometry: geometry,
              field: field,
              center: getCenter(geometry),
            });
          }
        });
      });
    }

    return currentGeometries;
  };

  await window
    .shp(currentData)
    .then(async (geoData) => {
      const shapes = await processGeoData(geoData);
      result = shapes;
    })
    .catch((_err) => onError(_err));
  return result;
};

export const processGeometries = async (currentData, onError, managementTool) => {
  const projection = await findProjection(currentData);
  let result = false;

  const processGeoData = async (geoData) => {
    const currentGeometries = [];
    let geometry = [];
    let utmCoordinates = false;
    let latLong = false;
    let proj4Conversion = false;
    let field = false;

    if (projection.sphere === "WGS 84") {
      projection.prj = WGS_84_PRJ;
    }

    if (Array.isArray(geoData)) {
      await geoData.forEach((geo) => {
        geo.features.forEach((feature) => {
          if (Array.isArray(feature.geometry.coordinates[0])) {
            field = feature.properties;

            feature.geometry.coordinates.forEach((coordinate) => {
              geometry = [];
              coordinate.forEach((item) => {
                if (typeof item[0] === "number" && typeof item[1] === "number") {
                  utmCoordinates = new UtmObj(projection.sphere);
                  latLong = utmCoordinates.convertUtmToLatLng(item[0], item[1], projection.zoneNum, projection.zoneLetter);
                  if (
                    latLong.lat > 90 ||
                    latLong.lat < -90 ||
                    latLong.lng > 90 ||
                    latLong.lat < -90 ||
                    projection.sphere === "Bessel 1841" ||
                    item.length >= 3
                  ) {
                    proj4Conversion = window.proj4(projection.prj, WGS_84_PRJ, [item[0], item[1]]);
                    geometry.push({
                      lat: proj4Conversion[1],
                      lng: proj4Conversion[0],
                    });
                  } else {
                    geometry.push(latLong);
                  }
                }
              });

              if (geometry.length > 0) {
                currentGeometries.push({
                  geometry: geometry,
                  field: field,
                  center: getCenter(geometry),
                });
              }
            });
          }
        });
        return currentGeometries;
      });
    } else {
      await geoData.features.forEach(async (feature) => {
        field = feature.properties;
        await feature.geometry.coordinates.forEach((coordinate) => {
          geometry = [];

          coordinate.forEach((item) => {
            if (typeof item[0] === "number" && typeof item[1] === "number" && managementTool === "365FarmNet") {
              geometry.push({
                lat: item[1],
                lng: item[0],
              });
            } else if (typeof item[0] === "number" && typeof item[1] === "number") {
              utmCoordinates = new UtmObj(projection.sphere);
              latLong = utmCoordinates.convertUtmToLatLng(item[0], item[1], projection.zoneNum, projection.zoneLetter);
              if (latLong.lat > 90 || latLong.lat < -90 || latLong.lng > 90 || latLong.lat < -90 || projection.sphere === "Bessel 1841" || item.length >= 3) {
                proj4Conversion = window.proj4(projection.prj, WGS_84_PRJ, [item[0], item[1]]);
                geometry.push({
                  lat: proj4Conversion[1],
                  lng: proj4Conversion[0],
                });
              } else {
                geometry.push(latLong);
              }
            }
          });
          if (geometry.length > 0) {
            currentGeometries.push({
              geometry: geometry,
              field: field,
              center: getCenter(geometry),
            });
          }
        });
      });
    }
    return currentGeometries;
  };

  await window
    .shp(currentData)
    .then(async (geoData) => {
      const shapes = await processGeoData(geoData);
      result = shapes;
    })
    .catch((_err) => onError(_err));
  return result;
};

class LRUProjectionCache {
  constructor(maxSize = 1000) {
    this.maxSize = maxSize;
    this.cache = new Map();
  }

  getKey(item, projection) {
    // using this to reinforce class method usage
    this.lastAccessed = Date.now();
    const key = `${item[0]}|${item[1]}|${projection.prj}|${projection.sphere}`;
    return key;
  }

  get(item, projection) {
    const key = this.getKey(item, projection);
    return this.cache.get(key);
  }

  set(item, projection, value) {
    const key = this.getKey(item, projection);
    this.cache.set(key, value);

    if (this.cache.size > this.maxSize) {
      // Delete oldest entry (similar to a queue clearing FIFO)
      this.cache.delete(this.cache.keys().next().value);
    }
  }
}

const projectionCache = new LRUProjectionCache();

// Generator-based Batch Processing
function* createBatches(features, batchSize = 50) {
  for (let i = 0; i < features.length; i += batchSize) {
    yield features.slice(i, i + batchSize);
  }
}

const transformCoordinate = (item, projection, _managementTool, isPolish) => {
  const cached = projectionCache.get(item, projection);
  if (cached) return cached;

  if (!needsUTMConversion(item, projection)) {
    const latLong = isPolish ? { lat: item[1], lng: item[0] } : { lat: item[0], lng: item[1] }; // 🚨 this is a hack to make it work for polish shapefiles (they are in lat/lng order)
    projectionCache.set(item, projection, latLong);
    return latLong;
  }

  const utmCoordinates = new UtmObj(projection.sphere);
  const latLongFromUTM = utmCoordinates.convertUtmToLatLng(item[0], item[1], projection.zoneNum, projection.zoneLetter);

  const converted = window.proj4(projection.prj, WGS_84_PRJ, [item[0], item[1]]);
  let isLatLongFromUTMOrConverted;

  if (
    latLongFromUTM.lat > 90 ||
    latLongFromUTM.lat < -90 ||
    latLongFromUTM.lng > 90 ||
    latLongFromUTM.lat < -90 ||
    projection.sphere === "Bessel 1841" ||
    item.length >= 3
  ) {
    isLatLongFromUTMOrConverted = { lat: converted[1], lng: converted[0] };
  } else {
    isLatLongFromUTMOrConverted = latLongFromUTM;
  }

  // N/b:  latLongFromUTM is most likely to be returned here (mostly DE shapefiles)
  // since polish shapefiles are using wgs84 (lat/lng) & utm is not needed
  const result = isPolish ? { lat: converted[0], lng: converted[1] } : isLatLongFromUTMOrConverted;
  return result;
};

const processFeature = (feature, projection, managementTool) => {
  if (!feature?.geometry?.coordinates) return [];

  const geometries = [];

  const isPolish = isPolishShapeFile(feature);

  let field = null;
  if (isPolish) {
    field = {
      ...feature.properties,
      customUniqueId: BboxUID.validate(feature.geometry?.bbox) ? BboxUID.encode(feature.geometry?.bbox) : BboxUID.encode(feature.geometry?.coordinates[0]),
    };
  } else {
    field = feature.properties;
  }

  for (const coordinate of feature.geometry.coordinates) {
    // eslint-disable-next-line no-continue
    if (!Array.isArray(coordinate)) continue;

    const geometry = [];
    for (const item of coordinate) {
      if (typeof item[0] === "number" && typeof item[1] === "number") {
        const point = transformCoordinate(item, projection, managementTool, isPolish);
        geometry.push(point);
      }
    }

    if (geometry.length > 0) {
      geometries.push({
        geometry,
        field,
        center: getCenter(geometry),
      });
    }
  }

  return geometries;
};

export const processGeometriesCombined = async (currentData, onError, managementTool, onProgress) => {
  try {
    const projection = await findProjection(currentData);
    if (projection.sphere === "WGS 84") {
      projection.prj = WGS_84_PRJ;
    }

    const geoData = await window.shp(currentData);
    if (!geoData) return [];

    const features = Array.isArray(geoData) ? geoData.flatMap((g) => g?.features || []) : geoData.features || [];

    const results = [];
    let processed = 0;

    for (const batch of createBatches(features)) {
      const batchResults = batch.flatMap((feature) => processFeature(feature, projection, managementTool));

      results.push(...batchResults.flat());
      processed += batch.length;

      // Yield to main thread every batch
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => {
        setTimeout(resolve, 0);
      });
      // Update progress if callback provided (good to have...maybe we can use it later on)
      onProgress?.(processed, features.length);
    }

    return results;
  } catch (err) {
    onError(err);
    return [];
  }
};

export const checkValidShapefileSize = (file) => file.size / 1024 / 1024 < 10;

export const updateMapFooterStyles = () => {
  let tryAgain = true;
  const mapFooters = document.getElementsByClassName("gm-style");

  for (const mapFooter of mapFooters) {
    if (mapFooter) {
      const mapFooterLinkBlock = mapFooter.children[mapFooter.children.length - 1];
      const mapFooterContainer = mapFooterLinkBlock.children[0];

      const addStyles = (el, styles) => Object.assign(el.style, styles);

      const containerStyling = {
        background: "linear-gradient(180deg, rgba(0, 0, 0, 0.1) 0%, rgba(0, 0, 0, 0.4) 100%)",
        width: "100%",
        height: "24px",
        lineHeight: "24px",
        paddingRight: "10px",
        alignItems: "center",
        justifyContent: "end",
      };

      const defaultStyling = {
        backgroundColor: "rgba(0, 0, 0, 0)",
        color: "white",
        lineHeight: "24px",
        height: "24px",
        fontWeight: "normal",
      };

      if (mapFooterContainer) {
        // set main container styling for the map footer
        addStyles(mapFooterContainer, containerStyling);

        // run through all children and set styling
        const mapFooterImprint = mapFooterContainer.children[1]?.children[0]?.children[0]?.children[1];
        if (mapFooterImprint) {
          tryAgain = false;
          addStyles(mapFooterImprint, defaultStyling);

          for (const block of mapFooterContainer.children) {
            addStyles(block, defaultStyling);

            for (const child of block.children) {
              addStyles(child, defaultStyling);

              if (child.children) {
                for (const childChild of child.children) {
                  addStyles(childChild, defaultStyling);
                  addStyles(childChild, { height: "auto" });

                  // remove elements in bottom layers because not used and creates weird spacing
                  if (childChild.textContent === "") {
                    addStyles(childChild, { display: "none" });
                  }

                  for (const childChildChild of childChild.children) {
                    addStyles(childChildChild, defaultStyling);
                    addStyles(childChildChild, { height: "auto" });
                  }
                }
              }
            }
          }
        }
      } else {
        tryAgain = true; // this is important for pages that have multiple maps
      }
    }
  }

  if (tryAgain) {
    setTimeout(() => {
      updateMapFooterStyles();
    }, 150);
  }
};

export const getDefaultSquare = (location) => {
  const lat = parseFloat(location.lat);
  const lng = parseFloat(location.lng);

  return [
    {
      lat: lat + 0.0007,
      lng: lng + 0.0009,
    },
    {
      lat: lat - 0.0007,
      lng: lng + 0.0009,
    },
    {
      lat: lat - 0.0007,
      lng: lng - 0.0009,
    },
    {
      lat: lat + 0.0007,
      lng: lng - 0.0009,
    },
  ];
};
